import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/admin/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Inbox,
  Settings,
  Newspaper,
  Award,
  Image as ImageIcon,
  Menu as MenuIcon,
  Home,
  LogOut,
  ExternalLink,
  Briefcase,
  Users,
  Search,
  Sparkles,
  FileText,
  BookOpen,
  Compass,
  HelpCircle,
} from "lucide-react";
import { useState } from "react";

const navItems = [
  { to: "/admin", label: "Dashboard", icon: Home, end: true },
  { to: "/admin/tutorial", label: "Tutorial", icon: HelpCircle },
  { to: "/admin/leads", label: "Leads Inbox", icon: Inbox, badge: true },
  { to: "/admin/applications", label: "Applications", icon: Briefcase },
  { to: "/admin/search-analytics", label: "Search Analytics", icon: Search },
  { to: "/admin/site-settings", label: "Site Settings", icon: MenuIcon },
  { to: "/admin/navigation", label: "Top Navigation", icon: Compass },
  { to: "/admin/pages", label: "Custom Pages", icon: FileText },
  { to: "/admin/site-pages", label: "Site Pages", icon: FileText },
  { to: "/admin/block-library", label: "Block Library", icon: BookOpen },
  { to: "/admin/why-page", label: "Why Antera Page", icon: FileText },
  { to: "/admin/team", label: "Team", icon: Users },
  { to: "/admin/features", label: "Features", icon: Sparkles },
  { to: "/admin/blog", label: "Blog Posts", icon: Newspaper },
  { to: "/admin/case-studies", label: "Case Studies", icon: Award },
  { to: "/admin/jobs", label: "Jobs / Careers", icon: Briefcase },
  { to: "/admin/media", label: "Media Library", icon: ImageIcon },
  { to: "/admin/settings", label: "Account", icon: Settings },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-layout" className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={`${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-40 w-64 bg-slate-900 text-slate-300 flex flex-col transition-transform duration-200`}
      >
        <div className="px-5 py-5 border-b border-slate-800">
          <Link to="/admin" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-sky-500/20 border border-sky-500/30 flex items-center justify-center">
              <span className="text-sm font-bold text-sky-400">A</span>
            </div>
            <div>
              <p className="text-sm font-bold text-white leading-tight">Antera CMS</p>
              <p className="text-[10px] text-slate-500 uppercase tracking-widest">Admin Console</p>
            </div>
          </Link>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-sky-500/15 text-sky-300 border border-sky-500/20"
                    : "text-slate-400 hover:bg-slate-800/60 hover:text-white border border-transparent"
                }`
              }
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <span className="flex-1">{item.label}</span>
              {item.soon && (
                <span className="text-[9px] font-bold uppercase tracking-wider text-slate-600 bg-slate-800 rounded px-1.5 py-0.5">
                  Soon
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-3 border-t border-slate-800 space-y-2">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="admin-view-site"
            className="flex items-center gap-2 text-xs text-slate-400 hover:text-white px-3 py-1.5 rounded-lg hover:bg-slate-800/60 transition-colors"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            View Live Site
          </a>
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center text-sky-300 text-xs font-bold">
              {user?.name?.charAt(0) || "A"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-white truncate">{user?.name}</p>
              <p className="text-[10px] text-slate-500 truncate">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="text-slate-500 hover:text-red-400 transition-colors p-1"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-20">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(true)}
            data-testid="admin-mobile-menu"
          >
            <MenuIcon className="h-5 w-5" />
          </Button>
          <span className="text-sm font-bold text-slate-900">Antera CMS</span>
          <div className="w-9" />
        </header>

        <main className="flex-1 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
